import { TAB_LINKS_HEIGHT } from "components/TabLinks";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { WasteListPageSurface } from "../WasteListPageSurface";
import { InvoiceImportDialog } from "./Import/InvoiceImportDialog";
import { InvoiceImportStatus } from "./Import/InvoiceImportStatus";
import { InvoicesTable } from "./List/InvoicesTable";

const IMPORT_RUN_PARAM = "invoiceImportRunId";

export function WasteInvoices() {
  const { tenantId } = useTenant();
  const { hasPermissions } = useAuthorization();
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const runId = searchParams.get(IMPORT_RUN_PARAM);
  const canImport = hasPermissions([Permission.WriteAllFacility]);

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Invoice Tracking" },
  ]);

  const setRunId = useCallback(
    (nextRunId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set(IMPORT_RUN_PARAM, nextRunId);
      navigate(
        { pathname: location.pathname, search: next.toString(), hash: location.hash },
        { replace: true }
      );
    },
    [location.hash, location.pathname, navigate, searchParams]
  );

  const clearRunId = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(IMPORT_RUN_PARAM);
    navigate(
      { pathname: location.pathname, search: next.toString(), hash: location.hash },
      { replace: true }
    );
  }, [location.hash, location.pathname, navigate, searchParams]);

  const chooseAnotherFile = useCallback(() => {
    clearRunId();
    setImportOpen(true);
  }, [clearRunId]);

  if (!tenantId) return null;

  return (
    <WasteListPageSurface headerOffset={TAB_LINKS_HEIGHT}>
      {runId && (
        <InvoiceImportStatus
          key={runId}
          runId={runId}
          canImport={canImport}
          onClose={clearRunId}
          onChooseAnother={chooseAnotherFile}
        />
      )}
      <InvoicesTable
        tenantId={tenantId}
        canImport={canImport}
        onImport={() => setImportOpen(true)}
      />
      {canImport && importOpen && (
        <InvoiceImportDialog
          open
          tenantId={tenantId}
          onClose={() => setImportOpen(false)}
          onQueued={setRunId}
        />
      )}
    </WasteListPageSurface>
  );
}
